import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils/cn';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md text-[13px] font-normal transition-colors duration-150 ease-out cursor-pointer disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-3.5 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-gray-400/30 dark:focus-visible:ring-gray-500/30",
  {
    variants: {
      variant: {
        default:
          'bg-gray-800 text-white hover:bg-gray-700 active:bg-gray-600 dark:bg-gray-300 dark:text-gray-900 dark:hover:bg-gray-400 dark:active:bg-gray-500',
        destructive:
          'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 focus-visible:ring-red-500/20 dark:bg-red-600 dark:hover:bg-red-500 dark:active:bg-red-400',
        outline:
          'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 active:bg-gray-100 dark:border-white/15 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white dark:active:bg-white/10',
        secondary:
          'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15 dark:active:bg-white/20',
        ghost:
          'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200 dark:bg-white/0 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-200 dark:active:bg-white/15',
        link: 'text-gray-900 underline-offset-4 hover:underline dark:text-gray-200',
      },
      size: {
        default: 'h-9 px-3 py-1.5',
        sm: 'h-8 rounded-md px-2.5 text-[12px]',
        lg: 'h-10 rounded-md px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return <Comp ref={ref} data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
});

Button.displayName = 'Button';

export { Button, buttonVariants };
